import Search from "@/components/search/Search";
import { Badge } from "@/components/ui/badge/Badge";
import { Button } from "@/components/ui/button/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import { Grid, GridItem } from "@/components/ui/grid/Grid";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination/Pagination";
import IAsset from "@/models/asset/Asset";
import { PaginatedResponse } from "@/models/helper/Response";
import { IRoom } from "@/models/room/Room";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

export type FetchAssetsParams = {
  room: IRoom | null;
  currentPage: number;
  searchTerm: string;
};

type Props = {
  open: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  room: IRoom | null;
  savedAssets: IAsset[];
  onSaveAssets: (selectedAssets: IAsset[]) => void;
  fetchAssetsFunction: (
    params: FetchAssetsParams
  ) => Promise<PaginatedResponse<IAsset[]>>;
};

const ChooseAssetsDialog = ({
  open,
  setIsOpen,
  room,
  savedAssets,
  onSaveAssets,
  fetchAssetsFunction,
}: Props) => {
  const [selectedAssets, setSelectedAssets] = useState(savedAssets);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [assets, setAssets] = useState<IAsset[]>([]);

  useEffect(() => {
    const fetchAssets = async () => {
      const res = await fetchAssetsFunction({ room, currentPage, searchTerm });

      setAssets(res.data);
      setLastPage(res.last_page);
    };
    // const fetchAssets = async () => {
    //   if (room === null) return;
    //   setLoading(true);
    //   const res = await AssetBorrowingService.getBorrowableAssetByRooms(
    //     userStore.data?.Email ?? "",
    //     "Assistant",
    //     currentPage,
    //     [parseInt(String(room.id))],
    //     searchTerm.trim() ? searchTerm.trim() : undefined
    //   );
    //   setAssets(res.data);
    //   setLastPage(res.last_page);
    //   setLoading(false);
    // };
    fetchAssets();
  }, [room, currentPage, searchTerm, fetchAssetsFunction]);

  useEffect(() => {
    if (open) setSelectedAssets(savedAssets);
  }, [open, savedAssets]);

  const handleSelect = (asset: IAsset) => {
    const isSelected = selectedAssets.some((a) => a.id === asset.id);
    const newSelectedAssets = isSelected
      ? selectedAssets.filter((a) => a.id !== asset.id)
      : [...selectedAssets, asset];

    setSelectedAssets(newSelectedAssets);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };

  const handleCancel = () => {
    setIsOpen(false);
    setSelectedAssets(savedAssets);
    setCurrentPage(1);
    setSearchTerm("");
  };

  const handleSave = async () => {
    setIsOpen(false);
    onSaveAssets(selectedAssets);
    setCurrentPage(1);
    setSearchTerm("");
  };

  // TODO: refactor
  const generatePaginationItems = () => {
    const pages = [];

    const showLeftEllipsis = currentPage > 3;
    const showRightEllipsis = currentPage < lastPage - 2;

    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(lastPage - 1, currentPage + 1);

    // First page
    pages.push(
      <PaginationItem key={1}>
        <PaginationLink
          href="#"
          isActive={currentPage === 1}
          onClick={(e) => {
            e.preventDefault();
            setCurrentPage(1);
          }}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );

    // Left ellipsis
    if (showLeftEllipsis) {
      pages.push(
        <PaginationItem key="left-ellipsis">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    // Main range (up to 3 pages)
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            href="#"
            isActive={i === currentPage}
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage(i);
            }}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Right ellipsis
    if (showRightEllipsis) {
      pages.push(
        <PaginationItem key="right-ellipsis">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    // Last page
    if (lastPage > 1) {
      pages.push(
        <PaginationItem key={lastPage}>
          <PaginationLink
            href="#"
            isActive={currentPage === lastPage}
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage(lastPage);
            }}
          >
            {lastPage}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return pages;
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="[&>button:first-child]:hidden p-4 pt-6 sm:p-6 max-w-[80%] max-h-[90%]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-bold text-xl">Choose Assets</DialogTitle>
        </DialogHeader>
        <Search onSearch={handleSearch} placeHolder="Search Asset Name" />
        <div className="flex flex-wrap gap-2 w-full text-md border border-input bg-background px-3 py-2 rounded-md">
          {selectedAssets.length === 0 ? (
            <>No assets selected</>
          ) : (
            selectedAssets.map((asset) => (
              <Badge
                className="flex gap-[0.4rem] items-center text-sm"
                variant={"secondary"}
              >
                <X
                  className="cursor-pointer"
                  size={"1.2rem"}
                  onClick={() => handleSelect(asset)}
                />{" "}
                <span>
                  {asset.id} - {asset.name}
                </span>
              </Badge>
            ))
          )}
        </div>
        <Grid className="overflow-auto" header="">
          {assets.length === 0 ? (
            <div>No Assets</div>
          ) : (
            assets.map((asset) => (
              <GridItem
                isSelected={selectedAssets.some((a) => a.id === asset.id)}
                key={asset.id}
                onClick={() => handleSelect(asset)}
              >
                <div className="w-full flex flex-col gap-2">
                  <div className="flex flex-col truncate">
                    <div className="font-semibold text-lg">{asset.name}</div>
                    <div className="text-sm">ID: {asset.id}</div>
                    <div className="text-xs"> {asset.year}</div>
                    <div className="text-xs">{asset.description}</div>
                    {/* <button
                      onClick={(event) => handleViewImage(asset.id, event)}
                      className="mt-2 p-1 bg-blue-500 text-sm text-white rounded text-center w-fit"
                    >
                      {loadingImage === asset.id ? "Loading..." : "View Image"}
                    </button> */}
                  </div>
                </div>
              </GridItem>
            ))
          )}
        </Grid>
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPage((prev) => prev - 1);
                }}
              />
            </PaginationItem>

            {generatePaginationItems()}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPage((prev) => prev + 1);
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
        <DialogFooter>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChooseAssetsDialog;
